import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./profileTheme";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetCexFeeRecordList } from "@/hooks/api/useGetCexFeeRecordList";
import {
  CexFeeRecordOrderStatus,
  type CexFeeBreakdown,
  type CexFeeCoinLeg,
  type CexFeeRecordCard,
} from "@/api/types";
import {
  formatCexFeeCoinSymbol,
  formatCexTradingPair,
  formatFeeAmount,
  formatFeeUsd,
  hasFeeBreakdown,
  isUsdtCoinSymbol,
} from "@/lib/cexFeeDisplay";
import { formatNumberTruncate } from "@/lib/utils";

function FeeRow({
  label,
  breakdown,
  sign,
  valueColor,
}: {
  label: string;
  breakdown?: CexFeeBreakdown;
  sign: "-" | "+";
  /** 退还/全额退回：标签与金额同为绿色（对齐 dapp） */
  valueColor?: string;
}) {
  const legs = breakdown?.legs ?? [];
  const total = breakdown?.totalUsdt ?? 0;
  if (legs.length === 0 && total <= 0) return null;

  const labelColor = valueColor ?? C.dim;
  const amountColor = valueColor ?? (sign === "+" ? C.green : C.text);

  const renderLeg = (leg: CexFeeCoinLeg, index: number) => {
    const symbol = formatCexFeeCoinSymbol(leg.coinSymbol);
    const amountStr = formatFeeAmount(leg.amount);
    return (
      <span key={index} className="mono-num" style={{ color: amountColor }}>
        {sign}
        {amountStr} {symbol}
        {!isUsdtCoinSymbol(leg.coinSymbol) && (leg.valueUsdt ?? 0) > 0 && (
          <span className="text-[9px] ml-0.5" style={{ color: C.dim }}>
            (≈${formatFeeUsd(leg.valueUsdt)})
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="flex justify-between gap-2">
      <span style={{ color: labelColor }}>{label}</span>
      <div className="text-right">
        {legs.length > 0 ? (
          legs.map(renderLeg)
        ) : (
          <span className="mono-num" style={{ color: amountColor }}>
            {sign}${formatFeeUsd(total)}
          </span>
        )}
      </div>
    </div>
  );
}

function FeeUsdRow({
  label,
  breakdown,
  sign,
  extraLabel,
}: {
  label: string;
  extraLabel?: string;
  breakdown?: CexFeeBreakdown;
  sign: "-" | "+";
}) {
  const total = breakdown?.totalUsdt ?? 0;
  if (total <= 0) return null;

  return (
    <div className="flex justify-between gap-2">
      <span style={{ color: C.dim }}>
        {label}
        {extraLabel ? ` ${extraLabel}` : ""}
      </span>
      <span
        className="mono-num text-right"
        style={{ color: sign === "+" ? C.green : C.text }}
      >
        {sign}${formatFeeUsd(total)}
      </span>
    </div>
  );
}

function FeeRecordCard({ fee }: { fee: CexFeeRecordCard }) {
  const { t } = useTranslation();
  const orderStatus = Number(fee.orderStatus);
  const isTraded = orderStatus === CexFeeRecordOrderStatus.Traded;
  const isStrategyOnly = orderStatus === CexFeeRecordOrderStatus.StrategyOnly;
  const isOrderFailed = orderStatus === CexFeeRecordOrderStatus.OrderFailed;

  const action = (fee.action || "").toLowerCase();
  const isLong = action === "buy";

  const scenarioLabel = () => {
    if (isTraded) return t("profilePage.feeHistory.scenarioTraded");
    if (isStrategyOnly) return t("profilePage.feeHistory.scenarioStrategyOnly");
    if (isOrderFailed) return t("profilePage.feeHistory.scenarioOrderFailed");
    return String(fee.orderStatus);
  };

  const scenarioColor = () => {
    if (isTraded) return C.green;
    if (isStrategyOnly) return C.amber;
    if (isOrderFailed) return C.red;
    return C.dim;
  };

  const scenarioBg = () => {
    if (isTraded) return "rgba(34,197,94,0.08)";
    if (isStrategyOnly) return "rgba(245,158,11,0.08)";
    if (isOrderFailed) return "rgba(239,68,68,0.08)";
    return C.card;
  };

  const pnl = Number(fee.pnl ?? 0);

  return (
    <div
      className="rounded-lg px-3 py-2"
      style={{ background: C.card, border: `1px solid ${C.border}` }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-semibold">
            {formatCexTradingPair(fee.symbol)}
          </span>
          {isTraded && fee.leverage > 0 && (
            <span
              className="text-[10px] px-1 py-0.5 rounded"
              style={{
                background: isLong
                  ? "rgba(34,197,94,0.10)"
                  : "rgba(239,68,68,0.10)",
                color: isLong ? C.green : C.red,
              }}
            >
              {isLong
                ? t("profilePage.feeHistory.sideLong")
                : t("profilePage.feeHistory.sideShort")}{" "}
              {fee.leverage}x
            </span>
          )}
          <span
            className="text-[9px] px-1.5 py-0.5 rounded-full"
            style={{
              background: scenarioBg(),
              color: scenarioColor(),
            }}
          >
            {scenarioLabel()}
          </span>
        </div>
        {isTraded && (
          <span
            className="mono-num text-[10px] font-medium shrink-0"
            style={{ color: pnl >= 0 ? C.green : C.red }}
          >
            {pnl >= 0 ? "+" : ""}
            {formatNumberTruncate(pnl, 3)}U
          </span>
        )}
      </div>

      <div className="space-y-0.5 text-[11px]">
        {hasFeeBreakdown(fee.prepaySignalFee) && (
          <FeeRow
            label={t("profilePage.feeHistory.prepaidCompute")}
            breakdown={fee.prepaySignalFee}
            sign="-"
          />
        )}

        {isTraded && hasFeeBreakdown(fee.platformFees) && (
          <FeeUsdRow
            label={t("profilePage.feeHistory.tradeFeesCombined")}
            extraLabel={t("profilePage.feeHistory.tradeFeesRateHint")}
            breakdown={fee.platformFees}
            sign="-"
          />
        )}

        {isTraded && hasFeeBreakdown(fee.computeFeeRefund) && (
          <div
            className="pt-0.5"
            style={{ borderTop: `1px dashed ${C.border}` }}
          >
            <FeeRow
              label={t("profilePage.feeHistory.refund")}
              breakdown={fee.computeFeeRefund}
              sign="+"
              valueColor={C.green}
            />
          </div>
        )}

        {isTraded && hasFeeBreakdown(fee.supplementFee) && (
          <div
            className="pt-0.5"
            style={{ borderTop: `1px dashed ${C.border}` }}
          >
            <FeeUsdRow
              label={t("profilePage.feeHistory.extraCharge")}
              breakdown={fee.supplementFee}
              sign="-"
            />
          </div>
        )}

        {isStrategyOnly && (
          <div className="text-[10px] pt-0.5" style={{ color: C.dim }}>
            {t("profilePage.feeHistory.strategyOnlyHint")}
          </div>
        )}

        {isOrderFailed && hasFeeBreakdown(fee.fullRefund) && (
          <FeeRow
            label={t("profilePage.feeHistory.fullRefund")}
            breakdown={fee.fullRefund}
            sign="+"
            valueColor={C.green}
          />
        )}
      </div>

      <div className="text-[9px] mt-1" style={{ color: C.dim }}>
        {fee.createTime ? dayjs(fee.createTime).format("YYYY-MM-DD HH:mm") : ""}
      </div>
    </div>
  );
}

export function FeeHistoryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetCexFeeRecordList(open);

  const records = data?.pages.flatMap(page => page.items ?? []) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 48 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        className="max-w-[420px]"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(245,158,11,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.amber }}>
            {t("profilePage.feeHistory.title")}
          </DialogTitle>
        </DialogHeader>

        <div
          className="space-y-1.5 overflow-y-auto pr-1"
          style={{ maxHeight: "min(55vh, 360px)" }}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className="text-center py-8 text-xs" style={{ color: C.dim }}>
              {t("profilePage.feeHistory.loading")}
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-8 text-xs" style={{ color: C.dim }}>
              {t("profilePage.feeHistory.empty")}
            </div>
          ) : (
            records.map(fee => (
              <FeeRecordCard
                key={
                  fee.chargeRecordId || `${fee.strategyId}-${fee.createTime}`
                }
                fee={fee}
              />
            ))
          )}
          {isFetchingNextPage && (
            <div
              className="text-center py-2 text-[10px]"
              style={{ color: C.dim }}
            >
              {t("profilePage.feeHistory.loadingMore")}
            </div>
          )}
        </div>

        {total > 0 && (
          <div
            className="text-center text-[10px] pt-1"
            style={{ color: C.dim }}
          >
            {t("profilePage.feeHistory.totalCount", { count: total })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
